import React from 'react'
import moment from 'moment'
import { t } from 'i18next'
import { Grid, IconButton } from '@material-ui/core'
import { Clear } from '@material-ui/icons'
import { DatePicker } from '@crystalservice/crystals-ui/lib/components/date-and-time-pickers/date-picker/date-picker'
import { DefaultSelectOption, SelectInput } from '@crystalservice/crystals-ui/lib/components/inputs/select-input/select-input'
import { TextField } from '@crystalservice/crystals-ui/lib/components/inputs/text-field/text-field'
import { ChildGender, Child, emptyChild } from './utils'
import { childNameRegexp } from '../../../utils/form-validation/validators/regexp-validator'
import { convertToNameFormat } from '../../../utils/string-utils'

const styles = require('./../registration-page.module.scss')

interface ChildrenRowProps {
    index: number
    child: Child
    childrenList: Child[]
    onValueChange?: (value: Child[], id: string) => void
    setChildrenList?: React.Dispatch<React.SetStateAction<Child[]>>
}

export const ChildrenRow: React.FC<ChildrenRowProps> = ({
    index,
    child,
    childrenList,
    onValueChange,
    setChildrenList,
}) => {
    const children = [...childrenList]
    const currentChildren = children[index]

    const now = moment().startOf('day')
    const birthdayMoment = moment(currentChildren.birthDate)
    const ageInYears = now.diff(birthdayMoment, 'year')

    const onDelete = (): void => {
        children.splice(index, 1)

        const isChildrenListEmpty: boolean = children.length === 0

        const updatedValue = !isChildrenListEmpty ? children : [emptyChild]
        onValueChange(updatedValue, 'children')
        setChildrenList(updatedValue)
    }

    const onNameChange = (name: string): void => {
        currentChildren.name = convertToNameFormat(name)
        children.splice(index, 1, currentChildren)

        onValueChange(children, 'children')
        setChildrenList(children)
    }

    const onGenderSelect = (selectedOption: DefaultSelectOption): void => {
        currentChildren.gender = selectedOption.value as ChildGender
        children.splice(index, 1, currentChildren)

        onValueChange(children, 'children')
        setChildrenList(children)
    }

    const onDateSelect = (date: Date | string): void => {
        let newDate = ''

        if (typeof date === "string") {
            newDate = date
        } else {
            newDate = date ? moment(date).format('YYYY-MM-DD') : null
        }

        currentChildren.birthDate = newDate
        children.splice(index, 1, currentChildren)

        onValueChange(children, 'children')
        setChildrenList(children)
    }

    const getNameErrorText = (value: string | number): string => {
        if (!value && value !== 0) return ''
        let stringValue: string = String(value)

        if (!childNameRegexp.test(stringValue)) {
            return 'Неверный формат имени'
        }

        return ''
    }

    return (
        <Grid
            id="childContainer"
            container
            item
            xs={12}
            spacing={1}
        >
            <Grid
                container
                item
                direction="row"
                wrap="nowrap"
                spacing={2}
                xs={12}
            >
                <Grid item xs={9}>
                    <p className={styles.childrenLabel}>
                        Имя
                    </p>
                    <TextField
                        className={styles.textField}
                        id="childName"
                        autoComplete="off"
                        value={child?.name}
                        onValueChange={onNameChange}
                        maxLength={50}
                        errorText={getNameErrorText(child?.name)}
                    />
                </Grid>
                <Grid item xs={3} style={{ margin: '30px 0px 0 0px' }}>
                    <Grid container item justify="flex-start" alignItems="flex-end">
                        <IconButton onClick={onDelete}>
                            <Clear color="primary" />
                        </IconButton>
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                container
                item
                direction="row"
                wrap="nowrap"
                xs={12}
                spacing={2}
            >
                <Grid item xs={3}>
                    <p className={styles.childrenLabel}>
                        Пол
                    </p>
                </Grid>
                <Grid item xs={6}>
                    <p className={styles.datePickerLabel}>
                        Дата рождения
                    </p>
                </Grid>
            </Grid>

            <Grid
                container
                item
                direction="row"
                wrap="nowrap"
                xs={12}
                spacing={2}
            >
                <Grid item xs={3}>
                    <SelectInput<DefaultSelectOption>
                        className={styles.select}
                        value={child.gender || ChildGender.Unknown}
                        options={[
                            {
                                value: ChildGender.Unknown,
                                label:  '–'
                            },
                            {
                                value: ChildGender.Male,
                                label: 'М'
                            },
                            {
                                value: ChildGender.Female,
                                label: 'Ж'
                            },
                        ]}
                        onSelect={onGenderSelect}
                    />
                </Grid>
                <Grid item xs={6}>
                    <DatePicker
                        className={styles.dateField}
                        minYear={1900}
                        defaultDate={
                            moment(child?.birthDate).isValid()
                                ? moment(child?.birthDate).toDate()
                                : null
                        }
                        onDateChange={onDateSelect}
                        outOfRangeText="Некорректное значение"
                        withYearSelect
                        disabledDateRanges={[
                            {
                                end: moment(0).set('year', 1900).startOf('year').toDate()
                            },
                            {
                                start: moment().add(1, 'day').toDate()
                            }
                        ]}
                        onInternalErrorChange={(error, date) => {
                            error ? onDateSelect(date || 'invalid') : onDateSelect(date)
                        }}
                        hideDateIcon
                        hideClearIcon
                    />
                </Grid>
                <Grid item xs={3}>
                    {currentChildren.birthDate && birthdayMoment && !isNaN(ageInYears) && (
                        <div className={styles.childrenAgeLabel}>
                            {
                                ageInYears >= 1
                                    ? t('registrationDetails.ageInYears.age', { count: ageInYears })
                                    : t('registrationDetails.ageInYears.lessYear')
                            }
                        </div>
                    )}
                </Grid>
            </Grid>
        </Grid>
    )
}
